import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Button from 'atoms/Button/Button';
import Dropdown from 'atoms/Dropdown/Dropdown';
import useModalVisibility from 'hooks/useModalVisibility';
import AlignJustifySvg from 'images/v5/icons/align-justify.svg';
import ArrowsDownSvg from 'images/v5/icons/arrows-down.svg';
import ArrowsUpSvg from 'images/v5/icons/arrows-up.svg';
import CalendarCheckSvg from 'images/v5/icons/calendar-check-02.svg';
import CalendarSvg from 'images/v5/icons/calendar.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import DownloadSvg from 'images/v5/icons/download-01.svg';
import FilterFunnelSvg from 'images/v5/icons/filter-funnel-02.svg';
import FilterSvg from 'images/v5/icons/filter-lines.svg';
import GridSvg from 'images/v5/icons/grid-01.svg';
import RefreshSvg from 'images/v5/icons/refresh-ccw-01.svg';
import SettingsSvg from 'images/v5/icons/settings-01.svg';
import ColorDot from 'layout/atoms/ColorDot';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import TabsButton from 'layout/atoms/TabsButton/TabsButton';
import { selectLayout, toggleSpecificationsDashboard } from 'store/layoutSlice';
import { fetchCategories, selectCategories, selectCategoriesPageInfo, selectCategoriesStatus, } from 'store/parameters/categoriesSlice';
import { fetchStatuses, selectStatuses, selectStatusesStatus, } from 'store/parameters/statusesSlice';
import { fetchRootFolder, fetchRootFolders, resetCategoryFilter as resetFoldersCategoryFilter, resetFilters as resetFoldersFilters, resetStatusFilter as resetFoldersStatusFilter, selectFilters as selectFoldersFilters, selectFoldersSpecifications, selectRootFolder, setFilter as setFoldersFilter, toggleCategoryFilter as toggleFoldersCategoryFilter, toggleStatusFilter as toggleFoldersStatusFilter, } from 'store/specificationsFoldersSlice';
import { resetCategoryFilter as resetSpecificationsCategoryFilter, resetFilters as resetSpecificationsFilters, resetStatusFilter as resetSpecificationsStatusFilter, selectFilters as selectSpecificationsFilters, setFilter as setSpecificationsFilter, toggleCategoryFilter as toggleSpecificationsCategoryFilter, toggleStatusFilter as toggleSpecificationsStatusFilter, } from 'store/specificationsSlice';
import downloadFile from 'utils/downloadUtils';
import getStatusLabel from 'utils/getStatusLabel';
import { getStatusColorVar } from 'utils/statusColors';
function Filters({ folders = false }) {
    const { t, i18n } = useTranslation(['specification', 'exceptions', 'common']);
    const [isModalVisible, handleShowModal, handleHideModal] = useModalVisibility(false);
    const dispatch = useDispatch();
    const location = useLocation();
    const layout = useSelector(selectLayout);
    const specifications = useSelector(selectFoldersSpecifications);
    const rootFolder = useSelector(selectRootFolder);
    const folderId = location.pathname.includes('/specifications/folders/')
        ? location.pathname.replace('/specifications/folders/', '')
        : rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId;
    const specificationsFilters = useSelector(selectSpecificationsFilters);
    const foldersFilters = useSelector(selectFoldersFilters);
    const statuses = useSelector(selectStatuses);
    const statusesFetchStatus = useSelector(selectStatusesStatus).fetch;
    const statusFilter = folders ? foldersFilters.statusFilter : specificationsFilters.statusFilter;
    const categories = useSelector(selectCategories);
    const categoriesPageInfo = useSelector(selectCategoriesPageInfo);
    const categoriesFetchStatus = useSelector(selectCategoriesStatus).fetch;
    const categoryFilter = folders
        ? foldersFilters.categoryFilter
        : specificationsFilters.categoryFilter;
    const [exporting, setExporting] = useState(false);
    const onClickExport = useCallback(() => {
        if (specifications.length > 0) {
            setExporting(true);
            downloadFile(`/export/getCategorySpecifications/${folderId}/${i18n.language}`).finally(() => setExporting(false));
        }
        else {
            handleShowModal();
        }
    }, [i18n.language, folderId, specifications.length, handleShowModal]);
    useEffect(() => {
        return () => {
            if (folders) {
                dispatch(resetFoldersFilters());
            }
            else {
                dispatch(resetSpecificationsFilters());
            }
        };
    }, [dispatch, folders]);
    useEffect(() => {
        if (statusesFetchStatus === 'idle') {
            dispatch(fetchStatuses());
        }
    }, [dispatch, statusesFetchStatus]);
    const handleToggleStatusFilter = (value) => {
        if (folders) {
            dispatch(toggleFoldersStatusFilter(value));
        }
        else {
            dispatch(toggleSpecificationsStatusFilter(value));
        }
    };
    useEffect(() => {
        // Lista kategorii jest paginowana, a filtr potrzebuje wszystkich, więc dociągamy kolejne strony.
        if (categoriesFetchStatus === 'idle' ||
            (categoriesFetchStatus === 'succeeded' && categoriesPageInfo.hasNextPage)) {
            dispatch(fetchCategories());
        }
    }, [dispatch, categoriesFetchStatus, categoriesPageInfo.hasNextPage]);
    const handleToggleCategoryFilter = (value) => {
        if (folders) {
            dispatch(toggleFoldersCategoryFilter(value));
        }
        else {
            dispatch(toggleSpecificationsCategoryFilter(value));
        }
    };
    const handleResetCategoryFilter = () => {
        if (folders) {
            dispatch(resetFoldersCategoryFilter());
        }
        else {
            dispatch(resetSpecificationsCategoryFilter());
        }
    };
    const handleResetStatusFilter = () => {
        if (folders) {
            dispatch(resetFoldersStatusFilter());
        }
        else {
            dispatch(resetSpecificationsStatusFilter());
        }
    };
    const handleSpecificationsSortingChange = (value) => {
        dispatch(setSpecificationsFilter({ filterName: 'orderBy', value }));
    };
    const handleFoldersSortingChange = (value, name, otherValue) => {
        dispatch(setFoldersFilter({ filterName: 'orderBy', value }));
        dispatch(setFoldersFilter({ filterName: 'orderName', value: name }));
        dispatch(setFoldersFilter({ filterName: 'othersOrderBy', value: otherValue }));
    };
    const specificationsSortingOptions = () => (React.createElement(React.Fragment, null,
        React.createElement(Button, { align: "left", theme: specificationsFilters.orderBy === 'modifiedDate' ? 'fill' : 'ghost', leftIcon: React.createElement(CalendarSvg, null), onClick: () => handleSpecificationsSortingChange('modifiedDate') }, t('common:modified_date')),
        React.createElement(Button, { align: "left", theme: specificationsFilters.orderBy === 'createdDate' ? 'fill' : 'ghost', leftIcon: React.createElement(CalendarCheckSvg, null), onClick: () => handleSpecificationsSortingChange('createdDate') }, t('common:created_date')),
        React.createElement(Button, { align: "left", theme: specificationsFilters.orderBy === 'name' ? 'fill' : 'ghost', leftIcon: React.createElement(SettingsSvg, null), onClick: () => handleSpecificationsSortingChange('name') }, t('common:name')),
        React.createElement("hr", null),
        React.createElement(Button, { align: "left", theme: !specificationsFilters.orderBy.startsWith('-') ? 'fill' : 'ghost', leftIcon: React.createElement(ArrowsUpSvg, null), onClick: () => specificationsFilters.orderBy.startsWith('-') &&
                handleSpecificationsSortingChange(specificationsFilters.orderBy.replace('-', '')) }, t('common:ascending')),
        React.createElement(Button, { align: "left", theme: specificationsFilters.orderBy.startsWith('-') ? 'fill' : 'ghost', leftIcon: React.createElement(ArrowsDownSvg, null), onClick: () => !specificationsFilters.orderBy.startsWith('-') &&
                specificationsFilters.orderBy !== '' &&
                handleSpecificationsSortingChange(`-${specificationsFilters.orderBy}`) }, t('common:descending'))));
    const foldersSortingOptions = () => (React.createElement(React.Fragment, null,
        React.createElement(Button, { align: "left", theme: foldersFilters.orderBy === 'modifiedDate' ? 'fill' : 'ghost', leftIcon: React.createElement(CalendarSvg, null), onClick: () => handleFoldersSortingChange('modifiedDate', t('common:modified_date'), 'modelSpecification.modifiedDate') }, t('common:modified_date')),
        React.createElement(Button, { align: "left", theme: foldersFilters.orderBy === 'createdDate' ? 'fill' : 'ghost', leftIcon: React.createElement(CalendarCheckSvg, null), onClick: () => handleFoldersSortingChange('createdDate', t('common:created_date'), 'modelSpecification.createdDate') }, t('common:created_date')),
        React.createElement(Button, { align: "left", theme: foldersFilters.orderBy === 'name' ? 'fill' : 'ghost', leftIcon: React.createElement(SettingsSvg, null), onClick: () => handleFoldersSortingChange('name', t('common:modified_date'), 'modelSpecification.name') }, t('common:name')),
        React.createElement("hr", null),
        React.createElement(Button, { align: "left", theme: !foldersFilters.orderBy.startsWith('-') ? 'fill' : 'ghost', leftIcon: React.createElement(ArrowsUpSvg, null), onClick: () => foldersFilters.orderBy.startsWith('-') &&
                handleFoldersSortingChange(foldersFilters.orderBy.replace('-', ''), t('common:modified_date'), foldersFilters.othersOrderBy.replace('-', '')) }, t('common:ascending')),
        React.createElement(Button, { align: "left", theme: foldersFilters.orderBy.startsWith('-') ? 'fill' : 'ghost', leftIcon: React.createElement(ArrowsDownSvg, null), onClick: () => !foldersFilters.orderBy.startsWith('-') &&
                foldersFilters.orderBy !== '' &&
                handleFoldersSortingChange(`-${foldersFilters.orderBy}`, t('common:modified_date'), `-${foldersFilters.othersOrderBy}`) }, t('common:descending'))));
    const statusFilterOptions = () => (React.createElement(React.Fragment, null,
        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(RefreshSvg, null), onClick: () => handleResetStatusFilter(), disabled: statusFilter.length === 0 }, t('common:reset')),
        React.createElement("hr", null),
        React.createElement(Button, { align: "left", leftIcon: React.createElement(ColorDot, { draft: true }), theme: statusFilter.includes('DRAFT') ? 'fill' : 'ghost', onClick: () => handleToggleStatusFilter('DRAFT') }, t('common:draft')),
        statuses.length > 0 && React.createElement("hr", null),
        statuses.map((item) => (React.createElement(Button, { key: item.id, align: "left", theme: statusFilter.includes(item.id) ? 'fill' : 'ghost', leftIcon: React.createElement(ColorDot, { color: getStatusColorVar(item.color) }), onClick: () => handleToggleStatusFilter(item.id) }, getStatusLabel(item, i18n)))),
        statuses.length > 0 && React.createElement("hr", null),
        React.createElement(Button, { align: "left", leftIcon: React.createElement(ColorDot, { color: "var(--semantic-text-onsubtle-positive)", check: true }), theme: statusFilter.includes('PRODUCTION') ? 'fill' : 'ghost', onClick: () => handleToggleStatusFilter('PRODUCTION') }, t('common:confirmed'))));
    const categoryFilterOptions = () => (React.createElement(React.Fragment, null,
        React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(RefreshSvg, null), onClick: () => handleResetCategoryFilter(), disabled: categoryFilter.length === 0 }, t('common:reset')),
        categories.length > 0 && React.createElement("hr", null),
        categories.map((item) => (React.createElement(Button, { key: item.id, align: "left", theme: categoryFilter.includes(item.id) ? 'fill' : 'ghost', onClick: () => handleToggleCategoryFilter(item.id) }, item.name)))));
    const viewTabs = [
        { id: 'grid', icon: React.createElement(GridSvg, null), squared: true },
        {
            id: 'list',
            icon: React.createElement(AlignJustifySvg, null),
            squared: true,
        },
    ];
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, rootFolder.status, rootFolder.rootFolderId]);
    return (React.createElement("div", { style: { display: 'flex', gap: '6px' } },
        React.createElement(Dropdown, { size: "m", closeOnClick: false, trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterFunnelSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) },
                t('common:status'),
                statusFilter.length > 0 ? ` (${statusFilter.length})` : '') }, statusFilterOptions()),
        React.createElement(Dropdown, { size: "m", closeOnClick: false, trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterFunnelSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) },
                t('specification:category'),
                categoryFilter.length > 0 ? ` (${categoryFilter.length})` : '') }, categoryFilterOptions()),
        React.createElement(Dropdown, { size: "m", trigger: React.createElement(Button, { theme: "border", leftIcon: React.createElement(FilterSvg, null), rightIcon: React.createElement(ChevronDownSvg, null) }, t('common:sorting')) }, folders ? foldersSortingOptions() : specificationsSortingOptions()),
        React.createElement(Button, { theme: "border", shape: "squared", leftIcon: React.createElement(DownloadSvg, null), onClick: onClickExport, loading: exporting }),
        React.createElement(TabsButton, { size: "s", tabs: viewTabs, activeTab: layout.specificationsDashboard, onClick: () => dispatch(toggleSpecificationsDashboard()) }),
        isModalVisible && (React.createElement(Modal, { name: "noSpecInFolder", size: "s", onClose: handleHideModal, header: React.createElement(Label, { weight: "strong" }, t('specification:no_specification_in_folder')) },
            React.createElement("div", null, t('specification:no_specification_in_folder_description'))))));
}
export default Filters;
